import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { isViewingAsSomeoneElse } from "@/lib/viewAs";
import { Button } from "@/components/ui/button";
import { ArrowRight, Globe, ShieldCheck, X } from "lucide-react";
import { brokerCredentialsService } from "@/services/brokerCredentials";
import {
  BROKER_SIGNUPS,
  PHEMEX_SELLING_POINTS,
  PHEMEX_SIGNUP_URL,
  shouldShowReferralBanner,
  shouldShowReferralInline,
} from "@/lib/phemexReferral";

const DISMISS_KEY = "copyfi.phemexReferralBanner.dismissed";

const POINT_ICONS = [ShieldCheck, Globe, Globe] as const;

function StartNowButton({ size = "default" }: { size?: "default" | "sm" }) {
  const { t } = useTranslation("copy");
  return (
    <Button
      size={size}
      className="shrink-0 gap-1.5"
      onClick={() => window.open(PHEMEX_SIGNUP_URL, "_blank", "noopener,noreferrer")}
    >
      {t("referral.startNow")}
      <ArrowRight className="h-4 w-4 rtl:-scale-x-100" />
    </Button>
  );
}

function Points({ className = "" }: { className?: string }) {
  const { t } = useTranslation("copy");
  return (
    <div className={`flex flex-wrap items-center gap-x-4 gap-y-1 ${className}`}>
      {PHEMEX_SELLING_POINTS.map((key, i) => {
        const Icon = POINT_ICONS[i] ?? Globe;
        return (
          <span key={key} className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Icon className="h-3.5 w-3.5 text-primary" />
            {t(key)}
          </span>
        );
      })}
    </div>
  );
}

/**
 * Sits inside the flow where someone is about to paste API keys. Neutral: every
 * partner exchange with its own link and what it is good for, none favoured
 * (Guillaume's /preview review #30, 2026-10-24).
 */
export function PhemexReferralInline({ className = "" }: { className?: string }) {
  const { t } = useTranslation("copy");
  if (!shouldShowReferralInline(PHEMEX_SIGNUP_URL)) return null;

  return (
    <div className={`rounded-lg border border-primary/20 bg-primary/5 p-3 space-y-2 ${className}`}>
      <div className="space-y-1">
        <p className="text-sm font-medium">{t("referral.neutralTitle")}</p>
        <p className="text-xs text-muted-foreground">{t("referral.neutralBody")}</p>
      </div>
      <div className="grid gap-2 sm:grid-cols-2">
        {BROKER_SIGNUPS.map((b) => (
          <div key={b.broker} className="rounded-md border border-border/50 p-2 flex flex-col gap-1.5">
            <p className="text-sm font-semibold">{b.broker}</p>
            <div className="flex flex-wrap gap-x-3 gap-y-0.5">
              {b.points.map((key) => (
                <span key={key} className="flex items-center gap-1 text-xs text-muted-foreground">
                  <ShieldCheck className="h-3 w-3 text-primary" />
                  {t(key)}
                </span>
              ))}
            </div>
            <Button size="sm" variant="outline" className="mt-auto gap-1.5"
              onClick={() => window.open(b.url, "_blank", "noopener,noreferrer")}>
              {t("referral.openWith", { broker: b.broker })}
              <ArrowRight className="h-3.5 w-3.5 rtl:-scale-x-100" />
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}

/** The app-wide reminder. Shown only to people who have no broker connected. */
export function PhemexReferralBanner() {
  const [dismissed, setDismissed] = useState(true);
  const [hasConnection, setHasConnection] = useState<boolean | null>(null);
  const { t } = useTranslation("copy");

  useEffect(() => {
    // Viewing someone else's account: show what THEY see. The dismissal lives
    // in this browser, so an admin who once closed it saw no banner on any
    // account he looked at (Guillaume, 2026-09-26).
    if (isViewingAsSomeoneElse()) {
      setDismissed(false);
      return;
    }
    try {
      setDismissed(localStorage.getItem(DISMISS_KEY) === "1");
    } catch {
      setDismissed(false); // private mode: show it rather than hide it forever
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    const check = async () => {
      try {
        const list = await brokerCredentialsService.getAllCredentials();
        if (!cancelled) setHasConnection((list || []).length > 0);
      } catch {
        // Treat a failed lookup as "already connected" so a transient error never paints a
        // signup banner over someone who has been trading here for months.
        if (!cancelled) setHasConnection(true);
      }
    };

    check();
    window.addEventListener("brokerConnectionChanged", check);
    return () => {
      cancelled = true;
      window.removeEventListener("brokerConnectionChanged", check);
    };
  }, []);

  const hide = () => {
    setDismissed(true);
    if (isViewingAsSomeoneElse()) return; // never saves the admin's own choice
    try {
      localStorage.setItem(DISMISS_KEY, "1");
    } catch {
      /* dismissal just does not persist */
    }
  };

  if (!shouldShowReferralBanner({ url: PHEMEX_SIGNUP_URL, dismissed, hasConnection })) return null;

  return (
    <div className="border-b border-primary/20 bg-primary/5 px-6 py-2.5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 flex-wrap items-center gap-x-4 gap-y-1">
          {/* Neutral, every partner (review #30, 10-24). */}
          <p className="text-sm font-medium">
            {t("referral.neutralTitle")}
          </p>
          <span className="text-xs text-muted-foreground">{t("referral.neutralBody")}</span>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          {BROKER_SIGNUPS.map((b) => (
            <Button key={b.broker} size="sm" variant="outline" className="gap-1.5"
              onClick={() => window.open(b.url, "_blank", "noopener,noreferrer")}>
              {b.broker}
              <ArrowRight className="h-3.5 w-3.5 rtl:-scale-x-100" />
            </Button>
          ))}
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={hide} aria-label={t("referral.dismiss")}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
